import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Link2, AlertTriangle } from "lucide-react";
import { HowItWorksSteps } from "../components/HowItWorks";
import { useCategories } from "@/context/SiteContentContext";
import { usePartner } from "@/context/PartnerContext";
import { useCopy } from "@/lib/copy";

export default function HowToOrder() {
  const cats = useCategories();
  const { t, C } = useCopy();
  const { isPartner, partner } = usePartner();
  const pgpAvailable = !isPartner || Boolean(partner?.pgp_public_key);
  const [flow, setFlow] = useState("shop");
  return (
    <div className="mx-auto max-w-4xl px-6 py-12" data-testid="how-to-order-page">
      <div className="mono text-[11px] uppercase tracking-[0.25em] text-primary">{t("howto_eyebrow")}</div>
      <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter sm:text-5xl">{t("howto_title")}</h1>
      <p className="mt-3 max-w-2xl text-lg text-muted-foreground"><C k="howto_intro" /></p>

      {/* Flow toggle */}
      <div className="mt-10" data-testid="hiw-animated-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-2xl font-bold">{t("howto_steps_title")}</h2>
          <div className="inline-flex border border-border" role="tablist">
            <button data-testid="hiw-flow-shop" onClick={() => setFlow("shop")}
              className={`mono px-4 py-2 text-[11px] uppercase tracking-[0.15em] transition-colors ${flow === "shop" ? "bg-primary text-primary-foreground" : "hover:text-primary"}`}>{cats.build.title}</button>
            <button data-testid="hiw-flow-custom" onClick={() => setFlow("custom")}
              className={`mono border-l border-border px-4 py-2 text-[11px] uppercase tracking-[0.15em] transition-colors ${flow === "custom" ? "bg-primary text-primary-foreground" : "hover:text-primary"}`}>{cats.deals.title}</button>
          </div>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          <C k={flow === "shop" ? "howto_flow_shop" : "howto_flow_custom"} />
        </p>
        <div className="mt-6 border border-border bg-card p-5">
          <HowItWorksSteps flow={flow} />
        </div>
      </div>

      {/* Sending delivery details privately */}
      <div className="mt-14" data-testid="howto-delivery">
        <h2 className="font-heading text-2xl font-bold">{t("howto_delivery_title")}</h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground"><C k="howto_delivery_intro" /></p>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {pgpAvailable && <div className="border border-primary/40 bg-primary/5 p-5" data-testid="howto-pgp">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <span className="font-semibold">{t("howto_pgp_title")}</span>
              <span className="mono rounded-full bg-primary/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-primary">{t("howto_pgp_badge")}</span>
            </div>
            <ol className="mt-3 list-decimal space-y-1.5 pl-4 text-sm text-muted-foreground">
              <li><C k="howto_pgp_step1" /></li>
              <li><C k="howto_pgp_step2" /></li>
              <li><C k="howto_pgp_step3" /></li>
            </ol>
            <p className="mt-3 text-xs text-muted-foreground"><C k="howto_pgp_note" /></p>
          </div>}

          <div className="border border-border bg-card p-5" data-testid="howto-temp">
            <div className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-primary" />
              <span className="font-semibold">{t("howto_temp_title")}</span>
              <span className="mono rounded-full bg-muted px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("howto_temp_badge")}</span>
            </div>
            <ol className="mt-3 list-decimal space-y-1.5 pl-4 text-sm text-muted-foreground">
              <li><C k="howto_temp_step1" /></li>
              <li><C k="howto_temp_step2" /></li>
              <li><C k="howto_temp_step3" /></li>
            </ol>
            <p className="mt-3 text-xs text-muted-foreground"><C k="howto_temp_note" /></p>
          </div>
        </div>

        {/* Email risk */}
        <div className="mt-3 flex items-start gap-3 border border-amber-500/40 bg-amber-500/10 p-4" data-testid="howto-email-risk">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div>
            <div className="font-semibold">{t("howto_email_title")}</div>
            <p className="mt-1 text-sm text-muted-foreground"><C k="howto_email_body" /></p>
          </div>
        </div>
      </div>

      {/* Reassurance */}
      <div className="mt-14 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[1, 2, 3].map((n) => (
          <div key={n} className="border border-border bg-card p-4">
            <div className="font-semibold">{t(`howto_card${n}_title`)}</div>
            <div className="mt-1 text-sm text-muted-foreground"><C k={`howto_card${n}_body`} /></div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-12 flex flex-col items-start gap-4 border border-primary bg-primary/10 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="font-heading text-xl font-bold">{t("howto_cta_title")}</div>
          <p className="mt-1 text-sm text-muted-foreground"><C k="howto_cta_body" /></p>
        </div>
        <Link to="/shop" data-testid="howto-start-cta" className="group inline-flex shrink-0 items-center gap-2 bg-primary px-6 py-3.5 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5">
          {t("howto_cta_button")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
}
